import {
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  Typography,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';

import './DetailsPage.scss';
import {
  useGetLandDetails,
  useGetRegridData,
  useGetRegridDataPartial,
} from '../../api/details';
import { useSearchParams } from 'react-router';
import { MapboxMap } from '../../possibly-stateful-components/Map/MapboxMap';
import { ShareModal } from '../../components/share-modal/share-modal.tsx';
import { useState } from 'react';
import { useGetCurrentUser } from '../../hooks/auth.ts';
import { useGetFavorites } from '../../api/favorites';
import { FavoriteButton } from '../../possibly-stateful-components/FavoriteButton/FavoriteButton';
import { FavoriteProperties } from '../../models/favorites.model';
import { GetInTouchButton } from './possibly-stateful-components/GetInTouchButton/GetInTouchButton';
import { RegridRequest } from '../../models/regrid.model';
import { DataModal } from './components/data-modal/data-modal.tsx';
import { ImageCarousel } from '../../possibly-stateful-components/ImageCarousel/ImageCarousel';
import { useGetLandSearchGooglePhoto } from '../../api/search';
import { topLeftText } from '../../utils/search.ts';
import { useQueryClient } from '@tanstack/react-query';
import { LandVisualizationModal } from '../../views/land-visualization-modal/land-visualization-modal.tsx';
import { useGetAdmin, useGetCustomer } from '../../api/contact';
import { translateZoningInfoNumberToString } from '../../utils/zoning-info.ts';
import { useTitle } from '../../hooks/useTitle.ts';
import { useGetLandSearchSingle } from '../../hooks/search.ts';

// TODO: This file is getting pretty long. We should look into splitting this page into smaller possibly-stateful-visualizations.
export const DetailsPage = () => {
  const [showingShareModal, setShowingShareModal] = useState(false);
  const [showingDataModal, setShowingDataModal] = useState(false);
  const [showingLandVisualizationModal, setShowingLandVisualizationModal] =
    useState(false);

  const [params] = useSearchParams();
  const id = params.get('id');

  const { isPaidCustomer, loading: loadingCustomer } = useGetCustomer();
  const { data: adminData, loading: loadingAdmin } = useGetAdmin();

  const queryClient = useQueryClient();
  // TODO: Remove this. This is a bad practice broadly and very brittle.
  // We set the query each time we do a new land search.
  // We'll use the query to check if we have results in the cache.
  // If not we'll use the id for a new search.
  const query = queryClient.getQueryData(['query']) || null;
  const { data: landSearchResponse } = useGetLandSearchSingle(
    (query as never) || { id: parseInt(id ?? '') }
  );
  const searchDataForChosenProperty = landSearchResponse?.data?.find(
    (property) => property.id === id
  );

  const searchType = landSearchResponse?.searchType ?? '';

  const { data: propertyData, loading } = useGetLandDetails({
    id: id!,
    searchType: searchType,
  });

  const user = useGetCurrentUser();
  const { data: favoritesData } = useGetFavorites(user.data?.userId ?? '');

  const photo = useGetLandSearchGooglePhoto(
    searchDataForChosenProperty,
    user.data
  );
  const imageURL =
    searchDataForChosenProperty &&
    searchDataForChosenProperty?.primaryListingImageUrl
      ? searchDataForChosenProperty?.primaryListingImageUrl
      : `data:image/png;base64,${photo.data?.base64Image}`;

  const lotAcres = propertyData?.lotSquareFeet
    ? (propertyData?.lotSquareFeet / 43560).toFixed(2)
    : 0;

  const builtAddress = () => {
    const addressInfo = propertyData?.address;
    return addressInfo
      ? `${addressInfo?.address}\n${addressInfo?.city}, ${addressInfo?.state} ${addressInfo?.zip}`
      : '';
  };

  useTitle(
    loading
      ? 'Loading Land Details - Immersive Homes'
      : `${builtAddress()} - Immersive Homes`
  );

  const regridRequest: RegridRequest | undefined = propertyData
    ? {
        address: builtAddress(),
        parcelnumb: propertyData?.apn || '',
        path: `us/${propertyData?.address.state}/${propertyData?.address.county.replace(/ /g, '-').replace('-County', '')}`,
      }
    : undefined;
  // Used for unpaid and paid customers
  const { data: regridDataPartial } = useGetRegridDataPartial(regridRequest);
  // Only used for paid customers (home placement, regrid data sheet, etc.)
  const {
    data: regridDataFull,
    error: regridErrorFull,
    loading: regridLoadingFull,
  } = useGetRegridData(regridRequest, isPaidCustomer, Boolean(adminData));

  const detailsListItem = (label: string, value: string) => {
    return (
      <li>
        <h5>{label}</h5>
        <Typography>{value}</Typography>
      </li>
    );
  };

  const lotFacts = () => {
    return (
      <div>
        <ul className="list">
          {detailsListItem(
            'Days Listed',
            `${propertyData?.mlsDaysOnMarket}` || '--'
          )}
          {detailsListItem(
            'Zoning Type:',
            regridDataPartial?.zoningInformation.zoningType ?? '--'
          )}
          {detailsListItem(
            'Zoning Subtype:',
            regridDataPartial?.zoningInformation.zoningSubtype ?? '--'
          )}
          {detailsListItem(
            'Zoning Code:',
            regridDataPartial?.zoningInformation.zoningCodeLink ?? '--'
          )}
          {detailsListItem(
            'County-Provided Acres:',
            `${regridDataPartial?.parcelData.countyProvidedAcres ?? '--'}`
          )}
          {detailsListItem(
            'County-Provided Parcel Square Feet:',
            `${regridDataPartial?.parcelData.countyProvidedParcelSquareFeet ?? '--'}`
          )}
          {detailsListItem(
            'Municipal Authority / County',
            propertyData?.address.county ?? ''
          )}
          {detailsListItem(
            'Height Restrictions:',
            translateZoningInfoNumberToString(
              regridDataPartial?.zoningInformation.maximumBuildingHeightFeet,
              ' ft.'
            ) ?? '--'
          )}
          {detailsListItem(
            'FEMA Flood Zone:',
            regridDataPartial?.floodZoneData.femaFloodZone ?? '--'
          )}
          {detailsListItem(
            'FEMA Flood Zone Subtype:',
            regridDataPartial?.floodZoneData.femaFloodZoneSubtype ?? '--'
          )}
          {detailsListItem('Wetland', 'Survey Required')}
        </ul>
      </div>
    );
  };

  const otherInfo = () => {
    return (
      <div>
        <ul className="list">
          {detailsListItem('Listed by:', propertyData?.agentOffice ?? '')}
          {detailsListItem('MLS #:', propertyData?.mlsNumber ?? '--')}
          {detailsListItem(
            'Elementary School District',
            regridDataPartial?.schoolInformation
              .censusProvidedElementarySchoolDistrict ?? '--'
          )}
          {detailsListItem(
            'Secondary School District',
            regridDataPartial?.schoolInformation
              .censusProvidedSecondarySchoolDistrict ?? '--'
          )}
          {detailsListItem(
            'Unified School District',
            regridDataPartial?.schoolInformation
              .censusProvidedUnifiedSchoolDistrict ?? '--'
          )}
          {detailsListItem(
            'Last Sale Date',
            searchDataForChosenProperty?.lastSaleDate ??
              propertyData?.lastSaleDate ??
              '--'
          )}
          {detailsListItem(
            'Last Sale Price',
            `$${
              searchDataForChosenProperty?.lastSaleAmount ||
              propertyData?.lastSaleAmount
                ? parseInt(
                    (searchDataForChosenProperty?.lastSaleAmount ??
                      propertyData?.lastSaleAmount) as string
                  )?.toLocaleString()
                : '--'
            }`
          )}
        </ul>
      </div>
    );
  };

  const regridDataButtons = () => {
    if (
      (!loadingCustomer && isPaidCustomer) ||
      (!loadingAdmin && Boolean(adminData))
    ) {
      if (regridLoadingFull) {
        return (
          <Button
            variant="outlined"
            aria-label="land evaluation and data sheet loading"
            disabled={true}
          >
            Loading data...
          </Button>
        );
      }
      if (regridErrorFull) {
        return (
          <Button
            variant="outlined"
            aria-label="land evaluation and data sheet unavailable"
            disabled={true}
          >
            Land Evaluation and Data Unavailable
          </Button>
        );
      }
      return (
        <>
          <Button
            aria-label="open Land Planning modal"
            data-is-signed-in={!!user.data?.userId}
            onClick={() => setShowingLandVisualizationModal(true)}
          >
            Evaluate Land
          </Button>
          <Button
            variant="outlined"
            aria-label="open data page modal"
            data-is-signed-in={!!user.data?.userId}
            onClick={() => setShowingDataModal(true)}
          >
            Data Sheet
          </Button>
        </>
      );
    }
  };

  const badgeText = topLeftText(propertyData?.mlsDaysOnMarket);

  const [showFavoriteMessageContainer, setShowFavoriteMessageContainer] =
    useState(false);
  const [showFavoriteMessage, setShowFavoriteMessage] = useState(false);

  const handleAddToFavorites = () => {
    setShowFavoriteMessageContainer(true);
    setTimeout(() => {
      setShowFavoriteMessageContainer(false);
    }, 2500);
    setShowFavoriteMessage(true);
    setTimeout(() => {
      setShowFavoriteMessage(false);
    }, 2000);
  };

  if (loading) {
    return (
      <div className="loading-full-screen">
        <CircularProgress sx={{ marginRight: '12px' }} />
        <Typography>Loading...</Typography>{' '}
      </div>
    );
  } else {
    return (
      <Box
        data-testid="details-page"
        className="details-page"
        sx={{ flex: 1, width: '100%' }}
      >
        <Box className="details-container">
          <Box className="left-container-column">
            <Box className="column-item">
              <div className="results-list-item">
                {badgeText && <Box className="badge">{badgeText}</Box>}
                {showFavoriteMessageContainer && (
                  <Box className="favorite-alert">
                    <Fade
                      in={showFavoriteMessage}
                      timeout={{ enter: 500, exit: 500 }}
                    >
                      <Button
                        variant="contained"
                        sx={{ cursor: 'default' }}
                        size="large"
                      >
                        Favorite added to My Portal
                      </Button>
                    </Fade>
                  </Box>
                )}
                <ImageCarousel
                  height="450px"
                  propertyData={propertyData}
                  propertySearchData={searchDataForChosenProperty}
                  mlsImage={imageURL}
                  key={'details-page'}
                  openBigModeOnClick
                />
              </div>
              <Box className="buttons-section">
                {regridDataButtons()}
                <IconButton
                  aria-label="share"
                  color="primary"
                  className="share-button"
                  onClick={() => setShowingShareModal(true)}
                >
                  <div className="button-tooltip">Share</div>
                  <ReplyIcon
                    sx={{ color: '#49454F', transform: 'scaleX(-1)' }}
                  />
                </IconButton>
                <FavoriteButton
                  id={id ?? ''}
                  favorites={favoritesData || []}
                  result={
                    {
                      latitude: propertyData?.latitude ?? 0,
                      longitude: propertyData?.longitude ?? 0,
                      apn: propertyData?.apn ?? '',
                      address: propertyData?.address || '',
                      id: id,
                    } as FavoriteProperties
                  }
                  addtoFavoritesFunc={handleAddToFavorites}
                />
              </Box>
            </Box>
            <Box className="column-item">
              <Box className="status-container">
                <span
                  style={{
                    fontSize: '24px',
                    color:
                      propertyData?.mlsStatus === 'Pending'
                        ? 'darkorange'
                        : propertyData?.mlsSold
                          ? 'orangered'
                          : 'green',
                  }}
                >
                  &#x2022;{' '}
                </span>
                <span className="status">
                  {propertyData?.mlsStatus === 'Pending'
                    ? 'Land Pending Sale'
                    : propertyData?.mlsSold
                      ? 'Sold Land'
                      : 'Land for sale'}
                </span>
              </Box>

              <Box className="land-info">
                <Box className="address">
                  <Typography variant="h2">{builtAddress()}</Typography>
                </Box>
                <Box className="price">
                  <h4>
                    $
                    {propertyData?.mlsSold
                      ? parseInt(
                          propertyData?.lastSaleAmount
                        )?.toLocaleString() || '--'
                      : propertyData?.mlsListingPrice.toLocaleString()}
                  </h4>
                  <span>
                    {propertyData?.lotSquareFeet.toLocaleString()} sqft{' '}
                    <span style={{ margin: '0 4px' }}>&#x2022;</span> {lotAcres}{' '}
                    acre
                  </span>
                </Box>
              </Box>
            </Box>
            <Box className="column-item">
              <Box className="agent-notes">
                <Typography variant="h3">Agent Notes</Typography>
                <p>
                  {searchDataForChosenProperty?.agentNotes ??
                    propertyData?.agentNotes ??
                    'No agent notes..'}
                </p>
              </Box>
            </Box>
            <Box className="column-item map">
              <MapboxMap
                addressDetails={propertyData}
                searchResponse={searchDataForChosenProperty}
                regridData={regridDataFull}
                dynamicResize
              />
            </Box>
          </Box>
          <Box className="right-container-column">
            <Box className="column-item filled-container">
              <Box data-details-retaining-ihd-broker>
                <Typography
                  variant="h2"
                  data-details-retaining-ihd-broker-header
                >
                  Why retaining an IHD broker matters:
                </Typography>
                <p data-details-retaining-ihd-broker-body>
                  Our team of specialists in system-built homes and land
                  integration can help streamline your land-purchase and
                  home-design by integrating these two essential elements during
                  the earliest stages of the process and continuing that support
                  from beginning-to-end. See the "Actions" menu above to contact
                  us today.
                </p>
                <Box className="get-in-touch">
                  <GetInTouchButton id={id ?? ''} searchType={searchType} />
                </Box>
              </Box>
            </Box>
            <Box className="column-item filled-container">
              <Typography variant="h2">Lot Details</Typography>
              {lotFacts()}
            </Box>
            <Box className="column-item filled-container">
              <Typography variant="h2">Additional Details</Typography>
              {otherInfo()}
            </Box>
          </Box>
        </Box>
        <ShareModal
          selectedPropertyId={`details?id=${id}`}
          isOpen={showingShareModal}
          setIsOpen={setShowingShareModal}
        />
        <DataModal
          isOpen={showingDataModal}
          setIsOpen={setShowingDataModal}
          regridData={regridDataFull}
          propertyData={propertyData}
          mlsImage={imageURL}
        />
        <LandVisualizationModal
          isOpen={showingLandVisualizationModal}
          setIsOpen={setShowingLandVisualizationModal}
          regridData={regridDataFull}
          propertyData={propertyData}
          mlsImage={imageURL}
        />
      </Box>
    );
  }
};
